import { SceneService, type SplitInfo } from '@src/services/SceneService';

import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { KaraokeTrackManager } from '../lib/KaraokeTrackManager';
import { PunchListTrackManager } from '../lib/PunchListTrackManager';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';
import { clampSceneAnchors } from './lib/clampSceneAnchors';
import { snapRangesToSceneBoundaries } from './lib/snapRangesToSceneBoundaries';
import { ShiftElementCommand } from './ShiftElementCommand';
import { TimelineMode } from './TimelineMode';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { Video } from '@src/types.ts/story';

/**
 * CropTimelineCommand
 * Keeps only media within [keepStart, keepEnd] (inclusive start, exclusive end semantics like typical timeline ranges).
 * Elements entirely outside range are removed.
 * Elements partially overlapping are trimmed (start and/or end) preserving relative media trim where possible.
 *
 * Undo restores removed elements and original properties for trimmed elements.
 */
export class CropTimelineCommand extends AbstractCommand {
  private keepRanges: { start: number; end: number }[];
  private executed = false;
  private trackIdsToSkip: string[] = [];
  private originalTimelineDuration: number | null = null;
  private transcriptTrackModified = false;
  private keepElementsForRange: Map<object, Set<string>> = new Map();
  private sceneService = new SceneService();
  private punchListElementIds: string[] = [];
  private splitIdMapping: Map<string, string> = new Map();
  private extraElementDataSnapshot: Video['extraElementData'] | null = null;
  private sceneSnapThreshold: number;

  constructor(
    editor: VideoEditor,
    keepRanges: { start: number; end: number }[],
    tracksToSkip: string[] = [],
    sceneSnapThreshold: number = 0,
  ) {
    super(editor);
    this.editor = editor;
    this.keepRanges = keepRanges;
    this.trackIdsToSkip = tracksToSkip;
    this.sceneSnapThreshold = Math.max(0, sceneSnapThreshold); // Ensure non-negative
  }

  getName(): string {
    return 'CropTimelineCommand';
  }

  getDescription(): string {
    return `Crop timeline to [${this.keepRanges[0].start}, ${this.keepRanges[0].end}] seconds`;
  }

  execute(): boolean {
    if (this.executed) return false;

    super.execute(); // makes a snapshot

    try {
      this.extraElementDataSnapshot = this.extraElementData.cloneValue();
      VideoEditorLogger.logCommand(this, 'start', {
        keepRanges: this.keepRanges,
      });

      this.captureOriginalTimelineDuration();
      this.adjustRangesToSceneBoundaries();
      this.validateRanges();
      this.cropElements();
      this.shiftTracks();

      this.adjustTimelineDuration();

      this.editor.markDirty('crop_timeline_command');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'timeline_cropped',
        keepRanges: this.keepRanges,
      });

      // Reconcile karaoke track if transcript track was modified
      if (this.transcriptTrackModified) {
        const karaokeTrack = this.editor.getKaraokeTrack();
        if (karaokeTrack) {
          const karaokeTrackManager = new KaraokeTrackManager(this.editor);
          karaokeTrackManager.reconcileKaraokeTrack();
        }
        const punchListTrackManager = new PunchListTrackManager(this.editor);
        punchListTrackManager.reconcilePunchList();
      }

      // Recalculate punch list positions if any punch list elements were shifted
      if (this.punchListElementIds.length > 0) {
        const punchListTrackManager = new PunchListTrackManager(this.editor);
        punchListTrackManager.recalculatePunchListPosition(
          this.punchListElementIds,
        );
      }

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', {
        keepRanges: this.keepRanges,
      });
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  private captureOriginalTimelineDuration() {
    this.originalTimelineDuration = this.editor.timeline.duration ?? null;
  }

  private validateRanges() {
    // snap to whole frame time
    this.keepRanges = this.keepRanges.map(range => ({
      start: this.editor.timeline.snapTimeToFrame(range.start),
      end: this.editor.timeline.snapTimeToFrame(range.end),
    }));

    // merge overlaping ranges and sort
    this.keepRanges.sort((a, b) => a.start - b.start);
    const mergedRanges: { start: number; end: number }[] = [];
    for (const range of this.keepRanges) {
      if (
        mergedRanges.length === 0 ||
        mergedRanges[mergedRanges.length - 1].end <= range.start
      ) {
        mergedRanges.push({ ...range });
      } else {
        mergedRanges[mergedRanges.length - 1].end = Math.max(
          mergedRanges[mergedRanges.length - 1].end,
          range.end,
        );
      }
    }
    this.keepRanges = mergedRanges;
  }

  /**
   * Adjusts keep ranges to snap to scene boundaries if within the threshold.
   * For each range boundary (start/end), checks if there's a scene boundary within N frames.
   * If found, snaps to that scene boundary.
   */
  private adjustRangesToSceneBoundaries() {
    this.keepRanges = snapRangesToSceneBoundaries(
      this.editor.timeline,
      this.keepRanges,
      this.sceneSnapThreshold,
    );
  }

  private cropElements() {
    const timeline = this.editor.timeline;
    const tracks = [...timeline.tracks];

    const fixPrecision = (num: number) => {
      return String(num).includes('.') ? parseFloat(num.toFixed(4)) : num;
    };
    for (const range of this.keepRanges) {
      this.keepElementsForRange.set(range, new Set<string>());
      for (const track of tracks) {
        if (this.trackIdsToSkip.includes(track.id)) {
          continue;
        }
        let nextElement = track.elements[0];
        while (nextElement) {
          // Outside entirely on left or right
          if (
            fixPrecision(nextElement.end_time) <= fixPrecision(range.start) ||
            fixPrecision(nextElement.time) >= fixPrecision(range.end)
          ) {
            if (nextElement instanceof VideoTranscriptElement) {
              this.transcriptTrackModified = true;
            }
            nextElement =
              track.elements[track.elements.indexOf(nextElement) + 1];
            // timeline.removeElement(el.id);
            // instead of removing -> not added to keepElementIds and will be removed in the end
          } else {
            const elementToKeep = this.handleOverlapping(nextElement, range);
            nextElement =
              track.elements[track.elements.indexOf(elementToKeep) + 1];
          }
        }
      }
    }

    // REMOVE elements
    const allElementsToKeep = [...this.keepElementsForRange.values()].flatMap(
      set => [...set],
    );

    // Reconcile video_template_settings before removing elements
    // (same pattern as scene anchor clamping, karaoke reconciliation, etc.)
    this.extraElementData.reconcileVideoTemplateSettings(
      new Set(allElementsToKeep),
      this.splitIdMapping,
    );

    // Handle scenes BEFORE removing elements (so we can resolve anchor positions)
    // Clamp scene anchors to kept element boundaries (handles both anchors being invalid)
    const clampedScenes = clampSceneAnchors(
      timeline.scenes,
      new Set(allElementsToKeep),
      timeline,
    );

    this.sceneService.replaceScenes(timeline, clampedScenes);

    // Now remove elements
    for (const track of tracks) {
      if (this.trackIdsToSkip.includes(track.id)) {
        continue;
      }
      track.elements.forEach(el => {
        if (!allElementsToKeep.includes(el.id)) {
          timeline.removeElement(el.id);
        }
      });
      if (track.elements.length === 0) {
        timeline.removeTrack(track.id);
      }
    }
    this.sceneService.validateAndAdjustScenes(timeline);
  }

  private adjustTimelineDuration() {
    const newTimelineDuration = this.keepRanges.reduce((acc, range) => {
      const rangeDuration = range.end - range.start;
      return acc + (rangeDuration > 0 ? rangeDuration : 0);
    }, 0);
    if (newTimelineDuration >= 0) {
      this.editor.timeline.setDuration(newTimelineDuration);
    }
  }

  private handleOverlapping(
    el: Element,
    range: { start: number; end: number },
  ): Element {
    const elStart = el.time;
    const elEnd = el.end_time;
    const newStart = Math.max(elStart, range.start);
    const newEnd = Math.min(elEnd, range.end);
    const epsilon = 0.001;
    if (el instanceof VideoTranscriptElement) {
      let elementToKeep = el;
      const trackManager = this.editor.getTranscriptTrackManager();
      if (Math.abs(newStart - elStart) > epsilon) {
        const splitInfo = this.captureSplitInfo(el, newStart);
        const parts = trackManager.splitElement(el, newStart);
        if (!parts) {
          throw new Error(
            'Failed to split transcript element during crop operation',
          );
        }
        this.sceneService.adjustScenesAfterSplit(this.editor.timeline, {
          ...splitInfo,
          leftElementId: parts[0].id,
          rightElementId: parts[1].id,
        });
        elementToKeep = parts?.at(1) ?? el;
        // trackManager.trimElementStart(el, newStart); split + delete instead of trimming to handle multiple ranges
        this.transcriptTrackModified = true;
      }
      if (Math.abs(newEnd - elEnd) > epsilon) {
        const splitInfo = this.captureSplitInfo(elementToKeep, newEnd);
        const parts = trackManager.splitElement(elementToKeep, newEnd);
        if (!parts) {
          throw new Error(
            'Failed to split transcript element during crop operation',
          );
        }
        this.sceneService.adjustScenesAfterSplit(this.editor.timeline, {
          ...splitInfo,
          leftElementId: parts[0].id,
          rightElementId: parts[1].id,
        });

        elementToKeep = parts?.at(0) ?? elementToKeep;
        // trackManager.trimElementEnd(el, newEnd);
        this.transcriptTrackModified = true;
      }
      this.keepElementsForRange.get(range)?.add(elementToKeep.id);
      return elementToKeep;
    } else {
      let elementToKeep = el;
      const originalId = el.id;
      const trackManager = this.editor.getTrackManager();
      if (Math.abs(newStart - elStart) > epsilon) {
        const parts = trackManager.splitElement(el, newStart);
        if (parts) {
          this.extraElementData.propagateOnSplit(el, parts[0], parts[1]);
        }
        elementToKeep = parts?.at(1) ?? el;
      }
      if (Math.abs(newEnd - elEnd) > epsilon) {
        const parts = trackManager.splitElement(elementToKeep, newEnd);
        if (parts) {
          this.extraElementData.propagateOnSplit(
            elementToKeep,
            parts[0],
            parts[1],
          );
        }
        elementToKeep = parts?.at(0) ?? elementToKeep;
      }
      // Track original -> final kept ID for template settings reconciliation
      if (elementToKeep.id !== originalId) {
        this.splitIdMapping.set(originalId, elementToKeep.id);
      }
      this.keepElementsForRange.get(range)?.add(elementToKeep.id);
      return elementToKeep;
    }
  }

  private shiftTracks() {
    // get a fresh list of tracks after potential removals above
    const tracksToShift = this.editor.timeline.tracks.filter(
      t => !this.trackIdsToSkip.includes(t.id),
    );
    let accumulatedDelta = 0;
    for (let i = 0; i < this.keepRanges.length; i++) {
      const previousRange = i > 0 ? this.keepRanges[i - 1] : null;
      const currentRange = this.keepRanges[i];
      accumulatedDelta += currentRange.start - (previousRange?.end ?? 0);

      for (const track of tracksToShift) {
        for (const el of track.sortedElements) {
          if (!this.keepElementsForRange.get(currentRange)?.has(el.id)) {
            continue;
          }
          if (el instanceof VideoTranscriptElement) {
            const shiftCommand = new ShiftElementCommand(
              this.editor,
              el,
              el.time - accumulatedDelta,
            );
            shiftCommand.execute();
            // this.editor
            //   .getTranscriptTrackManager()
            //   .shiftElement(
            //     el,
            //     el.time - accumulatedDelta,
            //     TimelineMode.DEFAULT,
            //     false,
            //   );
          } else if (el) {
            // Check if this is a punch list element
            const trackManager = this.editor.getTrackManager();
            if (trackManager.isPunchListElement(el.id)) {
              this.punchListElementIds.push(el.id);
            }
            this.editor.getTrackManager().shiftElement(
              el,
              el.time - accumulatedDelta,
              TimelineMode.DEFAULT,
              undefined, // targetTrackNumber - not changing tracks
              false, // snapToFrame
            );
          } else {
            VideoEditorLogger.logTimelineOperation(
              'crop_timeline_command__shift_tracks_warning',
              {
                error: 'Unexpected: track with no elements after removal',
                track,
              },
            );
          }
        }
      }
    }
  }

  /**
   * Captures split info BEFORE element is split (for VideoTranscriptElement only)
   */
  private captureSplitInfo(
    element: VideoTranscriptElement,
    splitTime: number,
  ): Omit<SplitInfo, 'leftElementId' | 'rightElementId'> {
    // Calculate split media time relative to element's media
    const splitMediaTime =
      element.getMediaStartTime() + (splitTime - element.time);

    return {
      originalElementId: element.id,
      originalMediaStart: element.getMediaStartTime(),
      originalMediaEnd: element.getMediaEndTime(),
      splitMediaTime,
    };
  }

  undo(): boolean {
    if (!this.executed) {
      return false;
    }

    const result = super.undo();

    // Restore extraElementData (video_template_settings, per-element metadata, etc.)
    if (result && this.extraElementDataSnapshot) {
      this.extraElementData.restoreValue(this.extraElementDataSnapshot);
    }

    // Restore original duration explicitly (snapshot restore may have recalculated based on shifted elements)
    if (result && this.originalTimelineDuration !== null) {
      try {
        this.editor.timeline.setDuration(this.originalTimelineDuration);
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        VideoEditorLogger.logTimelineOperation(
          'crop_timeline_command__restore_duration_fail',
          {
            error: err.message,
          },
        );
      }
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
